import React, { useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { ChevronDown, LayoutGrid, List, ListFilter, Star, Tags, Trash2, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLocation } from "react-router-dom";
import RootToggle from "./RootToggle";
import { formatPrice } from "../lib/utils";
import { productData } from "../data/productCatalog";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./ui/pagination";

const categories = [
  {
    value: "Placas de Video",
    label: "Placas de Video",
    subOptions: [
      { value: "Todos", label: "Todas" },
      { value: "NVIDIA", label: "Placas de Video GeForce" },
      { value: "AMD", label: "Placas de Video Radeon AMD" },
    ],
  },
  {
    value: "iPhones",
    label: "iPhones",
    subOptions: [
      { value: "Todos", label: "Todos" },
      { value: "iPhone 14", label: "iPhone 14" },
      { value: "iPhone 15", label: "iPhone 15" },
      { value: "iPhone 16", label: "iPhone 16" },
      { value: "iPhone 17", label: "iPhone 17" },
    ],
  },
  {
    value: "Almacenamiento",
    label: "Discos",
    comingSoon: true,
    subOptions: [
      { value: "Todos", label: "Todos" },
      { value: "SSD", label: "Discos SSD" },
      { value: "HDD", label: "Discos HDD" },
    ],
  },
  {
    value: "Memorias RAM",
    label: "Memorias RAM",
    comingSoon: true,
    subOptions: [
      { value: "Todos", label: "Todas" },
      { value: "DDR3", label: "Memorias DDR3" },
      { value: "DDR4", label: "Memorias DDR4" },
      { value: "DDR5", label: "Memorias DDR5" },
      { value: "SODIMM", label: "Memorias SODIMM" },
    ],
  },
  {
    value: "Fuentes",
    label: "Fuentes",
    comingSoon: true,
    subOptions: [{ value: "Todos", label: "Todas" }],
  },
];

const isComingSoonCategory = (value) =>
  categories.find((category) => category.value === value)?.comingSoon;

const BRAND_IMAGES = {
  ASUS: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?q=80&w=600&auto=format&fit=crop",
  MSI: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "MSI Ventus X2":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "MSI Ventus X3":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "MSI Ventus OC":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "MSI Gaming X":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  Gigabyte: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "Gigabyte OC":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "Gigabyte Vision":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "Gigabyte Eagle Triple Fan":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  EVGA: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "EVGA XC":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "EVGA XC3":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "EVGA FTW3 Ultra":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  Palit: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  PNY: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "Palit / PNY":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  Zotac: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "Zotac Gaming":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "Zotac Twin White":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  Aorus: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "Aorus Extreme":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "ASUS Dual":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "ASUS TUF":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "ASUS ROG Strix":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "ASUS Megalodon":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "XFX Quick":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  XFX: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  Sapphire:
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "Sapphire Pulse":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "Sapphire Nitro":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  ASRock:
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "ASRock Challenger":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "ASRock Phantom":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "ASRock Phantom Gaming":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  PowerColor:
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "PowerColor Red Devil":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  Battleaxe:
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  "Blower Turbo":
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  NVIDIA: "https://images.unsplash.com/photo-1587202372775-e229f172b9d7?q=80&w=600&auto=format&fit=crop",
  AMD: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
  default:
    "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=600&auto=format&fit=crop",
};

const CATEGORY_IMAGES = {
  "Placas de Video": "/images/catalog/gpu.avif",
  Fuentes: "/images/catalog/psu.avif",
  "Memorias RAM": "/images/catalog/ram.avif",
  Almacenamiento: "/images/catalog/storage.avif",
};

const SORT_OPTIONS = [
  { value: "destacados", title: "Mas vendidos" },
  { value: "mayor-precio", title: "Mayor precio" },
  { value: "menor-precio", title: "Menor precio" },
];

const PER_PAGE_OPTIONS = [
  { value: "6", title: "6 por página" },
  { value: "9", title: "9 por página" },
  { value: "12", title: "12 por página" },
];

const CATEGORY_FILTER_CONFIG = {
  "Placas de Video": [
    { key: "marca", label: "Marca", options: ["NVIDIA", "AMD"] },
    {
      key: "chipset",
      label: "Chipset GPU",
      options: [
        "RTX 3060",
        "RTX 3060 Ti",
        "RTX 3070",
        "RTX 3080",
        "RTX 2060",
        "RTX 2060 SUPER",
        "GTX 1660 SUPER",
        "RX 6700 XT",
        "RX 5700 XT",
        "RX 5600 XT",
        "RX 580",
      ],
    },
    { key: "memoria", label: "Memoria", options: ["6GB", "8GB", "12GB"] },
  ],
  Almacenamiento: [
    { key: "tipo", label: "Tipo", options: ["NVMe", "SATA", "HDD"] },
    { key: "tamano", label: "Tamaño", options: ['2.5"', '3.5"'] },
    {
      key: "velocidad",
      label: "Velocidad",
      options: [
        "5400 RPM",
        "7200 RPM",
        "SATA 3 Gb/s",
        "SATA 6 Gb/s",
        "NVMe PCIe 3.0",
        "NVMe PCIe 4.0",
        "NVMe PCIe 5.0",
      ],
    },
    {
      key: "generacion",
      label: "Generación M.2",
      options: ["M.2 Gen3", "M.2 Gen4", "M.2 Gen5"],
    },
    {
      key: "capacidad",
      label: "Capacidad",
      options: ["120GB", "240GB", "480GB", "500GB", "1TB", "2TB", "4TB"],
    },
  ],
  "Memorias RAM": [
    { key: "tipo", label: "Tipo", options: ["DDR3", "DDR4", "DDR5", "SODIMM"] },
    { key: "capacidad", label: "Capacidad", options: ["8GB", "16GB", "32GB", "64GB"] },
    {
      key: "velocidad",
      label: "Velocidad",
      options: ["2666 MHz", "3200 MHz", "5600 MHz", "6000 MHz", "6400 MHz", "7200 MHz"],
    },
  ],
  Fuentes: [
    {
      key: "potencia",
      label: "Potencia",
      options: ["500W", "550W", "600W", "650W", "700W", "800W", "900W"],
    },
    {
      key: "certificacion",
      label: "Certificación",
      options: ["80+ White", "80+ Bronze", "80+ Silver", "80+ Gold"],
    },
    {
      key: "modularidad",
      label: "Modularidad",
      options: ["Full Modular", "Semi Modular", "No modular"],
    },
  ],
  iPhones: [
    { key: "modelo", label: "Modelo", options: ["iPhone 14", "iPhone 15", "iPhone 16", "iPhone 17"] },
    { key: "almacenamiento", label: "Almacenamiento", options: ["128GB", "256GB", "512GB", "1TB"] },
    { key: "condicion", label: "Condición", options: ["Nuevo", "Sellado", "Reacondicionado"] },
  ],
};

const normalizeValue = (value) => String(value || "").trim().toLowerCase();

const getFilterHaystack = (product) =>
  [
    product.name,
    product.brand,
    product.category,
    product.subType,
    ...(product.subTypes || []),
    ...(product.variants?.map((variant) => variant.label) || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const getVariantForProduct = (product, selectedVariants) =>
  product.variants?.find((variant) => variant.key === selectedVariants[product.id]);

const getProductImageSrc = (product) =>
  product.image ||
  CATEGORY_IMAGES[product.category] ||
  BRAND_IMAGES[product.brand] ||
  BRAND_IMAGES[product.subType] ||
  BRAND_IMAGES.default;

const buildPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
  const ordered = Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < ordered.length; i += 1) {
    const page = ordered[i];
    const prev = ordered[i - 1];
    if (prev && page - prev > 1) {
      result.push(`ellipsis-${i}`);
    }
    result.push(page);
  }
  return result;
};

const CartControls = ({
  cartItem,
  isOutOfStock,
  isAtStockLimit,
  onIncrement,
  onDecrement,
  onRemove,
  addLabel,
  addClassName,
  wrapperClassName,
}) => {
  if (cartItem) {
    return (
      <div className={wrapperClassName}>
        {cartItem.quantity > 1 ? (
          <button
            type="button"
            className="h-8 w-8 rounded-md text-[#ff3b1f] font-semibold"
            onClick={onDecrement}
            aria-label="Restar cantidad"
          >
            -
          </button>
        ) : (
          <button
            type="button"
            className="h-8 w-8 rounded-md text-[#ff3b1f] font-semibold"
            onClick={onRemove}
            aria-label="Eliminar del carrito"
          >
            <Trash2 className="h-4 w-4 mx-auto" />
          </button>
        )}
        <span className="text-sm font-semibold">{cartItem.quantity}</span>
        <button
          type="button"
          className="h-8 w-8 rounded-md text-[#ff3b1f] font-semibold disabled:opacity-40"
          onClick={onIncrement}
          disabled={isOutOfStock || isAtStockLimit}
          aria-label="Sumar cantidad"
        >
          +
        </button>
      </div>
    );
  }

  return (
    <Button
      className={addClassName}
      onClick={onIncrement}
      type="button"
      disabled={isOutOfStock}
    >
      {isOutOfStock ? "Sin stock" : addLabel}
    </Button>
  );
};

const ProductCard = ({
  product,
  state,
  layout,
  onAddToCart,
  onIncrement,
  onDecrement,
  onRemove,
  formatPrice,
}) => {
  const {
    currentPrice,
    currentStock,
    cartItem,
    isOutOfStock,
    isAtStockLimit,
    imageSrc,
  } = state;

  if (layout === "mobile") {
    return (
      <Card className="plan-card overflow-hidden">
        <div className="p-4">
          <div className="rounded-md bg-secondary border border-border p-0 h-28 overflow-hidden">
            <img src={imageSrc} alt={product.name} className="h-full w-full object-cover" />
          </div>
          <h3 className="mt-3 text-sm font-semibold">{product.name}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{product.category}</p>
          <div className="mt-2 text-base font-bold text-foreground">
            {formatPrice(currentPrice)}
          </div>
        </div>
        <div className="px-4 pb-4">
          <CartControls
            cartItem={cartItem}
            isOutOfStock={isOutOfStock}
            isAtStockLimit={isAtStockLimit}
            onIncrement={cartItem ? onIncrement : onAddToCart}
            onDecrement={onDecrement}
            onRemove={onRemove}
            addLabel="Sumar al carrito"
            addClassName="w-full rounded-md bg-[#ff3b1f] text-white hover:bg-[#e2361c] py-2.5"
            wrapperClassName="flex items-center justify-between rounded-lg border border-[#ff3b1f]/40 px-2 py-1"
          />
        </div>
      </Card>
    );
  }

  if (layout === "list") {
    return (
      <Card className="plan-card p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-44 rounded-md bg-secondary border border-border p-0 h-28 overflow-hidden">
            {product.tag && (
              <span className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.25em] bg-white text-black px-2 py-0.5 rounded-md">
                {product.tag}
              </span>
            )}
            <img src={imageSrc} alt={product.name} className="h-full w-full object-cover" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="uppercase tracking-[0.25em]">{product.brand}</span>
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 text-primary" /> {product.rating}
              </span>
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">{product.name}</h3>
              <p className="text-xs text-muted-foreground mt-1">{product.category}</p>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Stock</span>
              <span>{currentStock <= 3 ? "Últimas unidades" : "Disponible"}</span>
            </div>
          </div>
          <div className="flex flex-col items-start gap-3 sm:items-end">
            <span className="text-lg font-bold text-foreground">
              {formatPrice(currentPrice)}
            </span>
            <CartControls
              cartItem={cartItem}
              isOutOfStock={isOutOfStock}
              isAtStockLimit={isAtStockLimit}
              onIncrement={cartItem ? onIncrement : onAddToCart}
              onDecrement={onDecrement}
              onRemove={onRemove}
              addLabel="Sumar"
              addClassName="rounded-md px-4 py-2.5 text-xs font-semibold bg-[#ff3b1f] text-white hover:bg-[#e2361c] shadow-lg"
              wrapperClassName="flex items-center justify-between gap-2 rounded-lg border border-[#ff3b1f]/40 px-2 py-1"
            />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="plan-card p-4 flex flex-col gap-4">
      <div className="relative rounded-md bg-secondary border border-border p-0 h-32 overflow-hidden">
        {product.tag && (
          <span className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.25em] bg-white text-black px-2 py-0.5 rounded-md">
            {product.tag}
          </span>
        )}
        <img src={imageSrc} alt={product.name} className="h-full w-full object-cover" />
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="uppercase tracking-[0.25em]">{product.brand}</span>
        <span className="flex items-center gap-1">
          <Star className="h-3 w-3 text-primary" /> {product.rating}
        </span>
      </div>

      <div>
        <h3 className="text-base font-semibold text-foreground">{product.name}</h3>
        <p className="text-xs text-muted-foreground mt-1">{product.category}</p>
      </div>

      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Stock</span>
        <span>{currentStock <= 3 ? "Últimas unidades" : "Disponible"}</span>
      </div>

      <div className="flex items-center justify-between mt-auto">
        <span className="text-lg font-bold text-foreground">{formatPrice(currentPrice)}</span>
        <CartControls
          cartItem={cartItem}
          isOutOfStock={isOutOfStock}
          isAtStockLimit={isAtStockLimit}
          onIncrement={cartItem ? onIncrement : onAddToCart}
          onDecrement={onDecrement}
          onRemove={onRemove}
          addLabel="Sumar"
          addClassName="rounded-md px-4 py-2.5 text-xs font-semibold bg-[#ff3b1f] text-white hover:bg-[#e2361c] shadow-lg"
          wrapperClassName="flex items-center justify-between gap-2 rounded-lg border border-[#ff3b1f]/40 px-2 py-1"
        />
      </div>
    </Card>
  );
};

const ShopSection = ({ headingLevel = "h2" }) => {
  const catalogProducts = useMemo(
    () =>
      productData.map((product) =>
        CATEGORY_IMAGES[product.category]
          ? { ...product, image: CATEGORY_IMAGES[product.category] }
          : product
      ),
    []
  );
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubFilters, setSelectedSubFilters] = useState(["Todos"]);
  const [openCategory, setOpenCategory] = useState(null);
  const [sortOption, setSortOption] = useState("destacados");
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  const [filtersByCategory, setFiltersByCategory] = useState({});
  const [selectedVariants, setSelectedVariants] = useState(() => {
    const initial = {};
    catalogProducts.forEach((product) => {
      if (product.variants?.length) {
        initial[product.id] = product.variants[0].key;
      }
    });
    return initial;
  });
  const { addItem, items, updateQuantity, removeItem } = useCart();
  const location = useLocation();
  const HeadingTag = headingLevel;
  const cartIndex = useMemo(() => {
    const map = new Map();
    items.forEach((entry) => {
      map.set(`${entry.id}:${entry.variantKey}`, entry);
    });
    return map;
  }, [items]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get("categoria") || params.get("category");
    const brandParam = params.get("marca");
    const modelParam = params.get("modelo");

    if (!categoryParam && !brandParam && !modelParam) {
      return;
    }

    let resolvedCategory =
      categories.find(
        (category) => normalizeValue(category.value) === normalizeValue(categoryParam)
      )?.value ||
      categories.find(
        (category) => normalizeValue(category.label) === normalizeValue(categoryParam)
      )?.value;

    if (!resolvedCategory && brandParam) {
      resolvedCategory = "Placas de Video";
    }

    if (!resolvedCategory && modelParam) {
      resolvedCategory = "iPhones";
    }

    if (!resolvedCategory || isComingSoonCategory(resolvedCategory)) {
      return;
    }

    setSelectedCategory(resolvedCategory);
    setSelectedSubFilters(["Todos"]);

    const nextFilters = {};
    if (brandParam) nextFilters.marca = [brandParam];
    if (modelParam) nextFilters.modelo = [modelParam];

    setFiltersByCategory((prev) => ({
      ...prev,
      [resolvedCategory]: nextFilters,
    }));

    if (location.hash) {
      const target = document.querySelector(location.hash);
      if (target) {
        setTimeout(() => {
          target.scrollIntoView({ behavior: "smooth" });
        }, 50);
      }
    }
  }, [location.search, location.hash]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedSubFilters, filtersByCategory, sortOption, itemsPerPage]);

  const toggleSubFilter = (value) => {
    setSelectedSubFilters((prev) => {
      if (value === "Todos") {
        return ["Todos"];
      }
      const next = prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev.filter((item) => item !== "Todos"), value];
      return next.length ? next : ["Todos"];
    });
  };

  const toggleFilterOption = (categoryKey, groupKey, option) => {
    setFiltersByCategory((prev) => {
      const categoryFilters = prev[categoryKey] || {};
      const current = categoryFilters[groupKey] || [];
      const exists = current.includes(option);
      const nextGroup = exists ? current.filter((item) => item !== option) : [...current, option];
      return {
        ...prev,
        [categoryKey]: {
          ...categoryFilters,
          [groupKey]: nextGroup,
        },
      };
    });
  };

  const removeFilterChip = (categoryKey, value) => {
    setFiltersByCategory((prev) => {
      const categoryFilters = prev[categoryKey] || {};
      const nextCategoryFilters = Object.fromEntries(
        Object.entries(categoryFilters).map(([groupKey, groupValues]) => [
          groupKey,
          groupValues.filter((item) => item !== value),
        ])
      );
      return { ...prev, [categoryKey]: nextCategoryFilters };
    });
  };

  const availableFilterOptions = useMemo(() => {
    if (!selectedCategory) {
      return {};
    }
    const baseProducts = catalogProducts.filter(
      (product) => product.category === selectedCategory
    );
    const activeGroups = filtersByCategory[selectedCategory] || {};
    const groups = CATEGORY_FILTER_CONFIG[selectedCategory] || [];

    return Object.fromEntries(
      groups.map((group) => {
        const activeValues = Object.entries(activeGroups)
          .filter(
            ([groupKey, values]) =>
              groupKey !== group.key && Array.isArray(values) && values.length
          )
          .flatMap(([, values]) => values);

        const scopedProducts = activeValues.length
          ? baseProducts.filter((product) => {
              const haystack = getFilterHaystack(product);
              return activeValues.some((value) =>
                haystack.includes(String(value).toLowerCase())
              );
            })
          : baseProducts;

        const options = group.options.filter((option) =>
          scopedProducts.some((product) =>
            getFilterHaystack(product).includes(String(option).toLowerCase())
          )
        );

        return [group.key, options.length ? options : group.options];
      })
    );
  }, [catalogProducts, selectedCategory, filtersByCategory]);

  const filteredProducts = useMemo(() => {
    const list = catalogProducts.filter((product) =>
      selectedCategory ? product.category === selectedCategory : true
    );

    const filterGroups = selectedCategory ? filtersByCategory[selectedCategory] || {} : {};
    const hasActiveFilters = Object.values(filterGroups).some(
      (values) => Array.isArray(values) && values.length > 0
    );

    const filteredByOptions = hasActiveFilters
      ? list.filter((product) => {
          const activeValues = Object.values(filterGroups).flat();
          const haystack = getFilterHaystack(product);
          return activeValues.some((value) =>
            haystack.includes(String(value).toLowerCase())
          );
        })
      : list;

    const sorted = [...filteredByOptions];
    if (sortOption === "mayor-precio") {
      sorted.sort((a, b) => b.basePrice - a.basePrice);
    }
    if (sortOption === "menor-precio") {
      sorted.sort((a, b) => a.basePrice - b.basePrice);
    }
    return sorted;
  }, [catalogProducts, selectedCategory, filtersByCategory, sortOption]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));

  useEffect(() => {
    setCurrentPage((prev) => Math.min(Math.max(prev, 1), totalPages));
  }, [totalPages]);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(start, start + itemsPerPage);
  }, [filteredProducts, currentPage, itemsPerPage]);

  const paginationItems = useMemo(
    () => buildPaginationItems(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const getVariant = (product) => getVariantForProduct(product, selectedVariants);

  const getProductState = (product) => {
    const variant = getVariant(product);
    const currentPrice = variant?.price ?? product.basePrice;
    const currentStock = variant?.stock ?? product.stock;
    const variantKey = variant?.key || "default";
    const cartItem = cartIndex.get(`${product.id}:${variantKey}`);
    const isOutOfStock = currentStock === 0;
    const isAtStockLimit =
      currentStock !== undefined && cartItem && cartItem.quantity >= currentStock;
    const imageSrc = getProductImageSrc(product);

    return {
      variant,
      currentPrice,
      currentStock,
      variantKey,
      cartItem,
      isOutOfStock,
      isAtStockLimit,
      imageSrc,
    };
  };

  const handleAddToCart = (product) => {
    const variant = getVariant(product);
    const variantLabel = variant?.label || "Versión estándar";
    const variantKey = variant?.key || "default";
    const price = variant?.price ?? product.basePrice;
    const stock = variant?.stock ?? product.stock;
    const imageSrc = getProductImageSrc(product);

    if (stock === 0) {
      return;
    }

    addItem({
      id: product.id,
      name: product.name,
      category: product.category,
      image: imageSrc,
      price,
      stock,
      variantLabel,
      variantKey,
    });
  };

  const scrollToCatalog = () => {
    const element = document.getElementById("catalogo");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="catalogo" className="py-16 bg-background text-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex text-center justify-center items-center gap-4 flex-col mb-10">
          <Badge>Tienda</Badge>
          <div className="flex gap-2 flex-col">
            <HeadingTag className="text-3xl md:text-5xl tracking-tighter max-w-3xl text-center font-regular">
              Elegí tu hardware con asesoría técnica
            </HeadingTag>
            <p className="text-lg leading-relaxed tracking-tight text-muted-foreground max-w-3xl text-center">
              Filtros inteligentes, variantes disponibles y recomendaciones claras para cada presupuesto.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
          <div className="lg:hidden space-y-3">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 rounded-lg border border-primary/40 text-xs font-semibold text-primary bg-transparent hover:bg-transparent"
                onClick={() => setIsCategoryOpen(true)}
              >
                <Tags className="mr-2 h-3.5 w-3.5" />
                Categorías
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1 rounded-lg border border-primary/40 text-xs font-semibold text-primary bg-transparent hover:bg-transparent"
                onClick={() => setIsFilterOpen(true)}
              >
                <ListFilter className="mr-2 h-3.5 w-3.5" />
                Filtros
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1 rounded-lg border border-primary/40 text-xs font-semibold text-primary bg-transparent hover:bg-transparent"
                onClick={() => setIsSortOpen(true)}
              >
                <ChevronDown className="mr-2 h-3.5 w-3.5" />
                Ordenar
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <RootToggle
                value={String(itemsPerPage)}
                onValueChange={(value) => setItemsPerPage(Number(value))}
                options={PER_PAGE_OPTIONS}
                placeholder="Por página"
                className="flex-1 justify-between rounded-lg border border-primary/40 text-xs font-semibold text-primary"
              />
              <div className="ml-auto flex items-center gap-1 rounded-lg border border-primary/40 px-1 py-1 text-primary">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  aria-pressed={viewMode === "grid"}
                  className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition ${
                    viewMode === "grid"
                      ? "bg-[#ff3b1f] text-white"
                      : "text-[#ff3b1f]"
                  }`}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  aria-pressed={viewMode === "list"}
                  className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition ${
                    viewMode === "list"
                      ? "bg-[#ff3b1f] text-white"
                      : "text-[#ff3b1f]"
                  }`}
                >
                  <List className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selectedCategory && (
                <button
                  type="button"
                  onClick={() => {
                    setFiltersByCategory((prev) => ({
                      ...prev,
                      [selectedCategory]: {},
                    }));
                  }}
                  className="rounded-full"
                >
                  <Badge className="bg-primary text-primary-foreground border-none">
                    {selectedCategory}
                  </Badge>
                </button>
              )}
              {selectedCategory &&
                Array.from(
                  new Set(
                    Object.values(filtersByCategory[selectedCategory] || {}).flat()
                  )
                ).map((filter) => (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => removeFilterChip(selectedCategory, filter)}
                    className="rounded-full"
                  >
                    <Badge variant="outline" className="border-primary/30 text-primary">
                      {filter}
                    </Badge>
                  </button>
                ))}
            </div>
          </div>

          {isCategoryOpen && (
            <div
              className="lg:hidden fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-10"
              onClick={() => setIsCategoryOpen(false)}
            >
              <div
                className="w-full max-w-sm overflow-hidden bg-transparent"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Tags className="h-4 w-4 text-primary" />
                    Categorías
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsCategoryOpen(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="max-h-[70vh] overflow-y-auto px-4 py-4 space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category.value}
                      type="button"
                      disabled={category.comingSoon}
                      onClick={() => {
                        if (category.comingSoon) return;
                        setSelectedCategory(category.value);
                        setSelectedSubFilters(["Todos"]);
                        setIsCategoryOpen(false);
                      }}
                      className={`w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${
                        selectedCategory === category.value
                          ? "text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      } ${category.comingSoon ? "cursor-not-allowed opacity-60 hover:text-muted-foreground" : ""}`}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span>{category.label}</span>
                        {category.comingSoon && (
                          <span className="rounded-full border border-border/60 bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                            Próximamente
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isFilterOpen && (
            <div
              className="lg:hidden fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-10"
              onClick={() => setIsFilterOpen(false)}
            >
              <div
                className="w-full max-w-sm overflow-hidden bg-transparent"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <ListFilter className="h-4 w-4 text-primary" />
                    Filtros
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsFilterOpen(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="max-h-[70vh] overflow-y-auto px-4 py-4 space-y-4">
                  {!selectedCategory ? (
                    <div className="text-xs text-muted-foreground">
                      Los filtros de producto son aplicables a las subcategorías seleccionadas.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {(CATEGORY_FILTER_CONFIG[selectedCategory] || []).map((group) => (
                        <div key={group.key} className="space-y-2">
                          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                            {group.label}
                          </p>
                          {(availableFilterOptions[group.key] || group.options).map((option) => (
                            <label key={option} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={
                                  filtersByCategory[selectedCategory]?.[group.key]?.includes(option) ||
                                  false
                                }
                                onChange={() =>
                                  toggleFilterOption(selectedCategory, group.key, option)
                                }
                                className="accent-primary"
                              />
                              {option}
                            </label>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="border-t border-border px-4 py-3">
                  <Button
                    type="button"
                    className="w-full rounded-lg bg-transparent text-foreground border border-border hover:bg-black hover:text-white active:bg-black active:text-white"
                    onClick={() => setIsFilterOpen(false)}
                  >
                    Aplicar filtros
                  </Button>
                </div>
              </div>
            </div>
          )}

          {isSortOpen && (
            <div
              className="lg:hidden fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-10"
              onClick={() => setIsSortOpen(false)}
            >
              <div
                className="w-full max-w-sm overflow-hidden bg-transparent"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <ChevronDown className="h-4 w-4 text-primary" />
                    Ordenar por
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsSortOpen(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="px-4 py-4 space-y-2">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setSortOption(option.value);
                        setIsSortOpen(false);
                      }}
                      className={`w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${
                        sortOption === option.value
                          ? "bg-primary text-primary-foreground"
                          : "bg-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {option.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <aside className="space-y-6 hidden lg:block">
            <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
              <h3 className="text-base font-semibold text-foreground">Categorías</h3>
              <div className="mt-4 space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.value}
                    type="button"
                    disabled={category.comingSoon}
                    onClick={() => {
                      if (category.comingSoon) return;
                      setSelectedCategory(category.value);
                      setSelectedSubFilters(["Todos"]);
                    }}
                    className={`w-full rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${
                      selectedCategory === category.value
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    } ${category.comingSoon ? "cursor-not-allowed opacity-60 hover:text-muted-foreground" : ""}`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span>{category.label}</span>
                      {category.comingSoon && (
                        <span className="rounded-full border border-border/60 bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                          Próximamente
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-0 space-y-4">
              <h3 className="text-base font-semibold text-foreground">Filtros</h3>
              {!selectedCategory ? (
                <div className="text-xs text-muted-foreground">
                  Los filtros de producto son aplicables a las subcategorías seleccionadas.
                </div>
              ) : (
                <>
                  {(CATEGORY_FILTER_CONFIG[selectedCategory] || []).map((group) => (
                    <div key={group.key} className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        {group.label}
                      </p>
                      {(availableFilterOptions[group.key] || group.options).map((option) => (
                        <label key={option} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={
                              filtersByCategory[selectedCategory]?.[group.key]?.includes(option) ||
                              false
                            }
                            onChange={() =>
                              toggleFilterOption(selectedCategory, group.key, option)
                            }
                            className="accent-primary"
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>
          </aside>

          <div className="space-y-6">
            <div className="hidden lg:flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              
              <div className="flex flex-wrap items-center gap-3">
                <RootToggle
                  value={sortOption}
                  onValueChange={setSortOption}
                  options={SORT_OPTIONS}
                  className="min-w-[150px]"
                />
                <RootToggle
                  value={String(itemsPerPage)}
                  onValueChange={(value) => setItemsPerPage(Number(value))}
                  options={PER_PAGE_OPTIONS}
                  placeholder="Productos por página"
                  className="min-w-[180px]"
                />
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("grid")}
                    aria-pressed={viewMode === "grid"}
                    className={`flex h-9 items-center gap-1 rounded-md px-3 text-xs font-semibold transition ${
                      viewMode === "grid"
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                    Cuadrícula
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("list")}
                    aria-pressed={viewMode === "list"}
                    className={`flex h-9 items-center gap-1 rounded-md px-3 text-xs font-semibold transition ${
                      viewMode === "list"
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <List className="h-3.5 w-3.5" />
                    Lista
                  </button>
                </div>
                
              </div>
            </div>
            <div className="mt-16 mb-8 hidden lg:flex items-center px-4 text-sm text-muted-foreground">
              <span>
                Mostrando {Math.min(paginatedProducts.length, filteredProducts.length)} de {filteredProducts.length} productos
              </span>
            </div>
            {filteredProducts.length === 0 ? (
              <div className="hidden lg:flex items-center justify-center rounded-3xl border border-dashed border-border bg-card/40 p-12 text-center text-sm text-muted-foreground">
                No hay productos disponibles con los filtros seleccionados.
              </div>
            ) : (
              <>
                {viewMode === "grid" ? (
                  <div className="hidden lg:grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {paginatedProducts.map((product) => {
                      const {
                        currentPrice,
                        currentStock,
                        variantKey,
                        cartItem,
                        isOutOfStock,
                        isAtStockLimit,
                        imageSrc,
                      } = getProductState(product);

                      return (
                        <ProductCard
                          key={product.id}
                          product={product}
                          state={{
                            currentPrice,
                            currentStock,
                            cartItem,
                            isOutOfStock,
                            isAtStockLimit,
                            imageSrc,
                          }}
                          layout="grid"
                          onAddToCart={() => handleAddToCart(product)}
                          onIncrement={() => updateQuantity(product.id, variantKey, 1)}
                          onDecrement={() => updateQuantity(product.id, variantKey, -1)}
                          onRemove={() => removeItem(product.id, variantKey)}
                          formatPrice={formatPrice}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="hidden lg:flex flex-col gap-4">
                    {paginatedProducts.map((product) => {
                      const {
                        currentPrice,
                        currentStock,
                        variantKey,
                        cartItem,
                        isOutOfStock,
                        isAtStockLimit,
                        imageSrc,
                      } = getProductState(product);

                      return (
                        <ProductCard
                          key={`${product.id}-list`}
                          product={product}
                          state={{
                            currentPrice,
                            currentStock,
                            cartItem,
                            isOutOfStock,
                            isAtStockLimit,
                            imageSrc,
                          }}
                          layout="list"
                          onAddToCart={() => handleAddToCart(product)}
                          onIncrement={() => updateQuantity(product.id, variantKey, 1)}
                          onDecrement={() => updateQuantity(product.id, variantKey, -1)}
                          onRemove={() => removeItem(product.id, variantKey)}
                          formatPrice={formatPrice}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
            <div className="mt-10 lg:hidden flex items-center px-4 text-sm text-muted-foreground">
              <span>
                Mostrando {Math.min(paginatedProducts.length, filteredProducts.length)} de {filteredProducts.length} productos
              </span>
            </div>
            {filteredProducts.length === 0 ? (
          <div className="mt-8 lg:hidden rounded-2xl border border-dashed border-border bg-card/40 p-8 text-center text-sm text-muted-foreground">
            No hay productos disponibles con los filtros seleccionados.
          </div>
        ) : (
          <div
            className={`mt-8 grid gap-4 lg:hidden ${
              viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"
            }`}
          >
            {paginatedProducts.map((product) => {
              const {
                currentPrice,
                currentStock,
                variantKey,
                cartItem,
                isOutOfStock,
                isAtStockLimit,
                imageSrc,
              } = getProductState(product);

              return (
                <ProductCard
                  key={`${product.id}-mobile`}
                  product={product}
                  state={{
                    currentPrice,
                    currentStock,
                    cartItem,
                    isOutOfStock,
                    isAtStockLimit,
                    imageSrc,
                  }}
                  layout="mobile"
                  onAddToCart={() => handleAddToCart(product)}
                  onIncrement={() => updateQuantity(product.id, variantKey, 1)}
                  onDecrement={() => updateQuantity(product.id, variantKey, -1)}
                  onRemove={() => removeItem(product.id, variantKey)}
                  formatPrice={formatPrice}
                />
              );
            })}
          </div>
        )}
        {filteredProducts.length > 0 && totalPages > 1 && (
          <div className="mt-14">
            <Pagination>
              <PaginationContent className="justify-center">
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      setCurrentPage((prev) => Math.max(1, prev - 1));
                      scrollToCatalog();
                    }}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
                {paginationItems.map((item) =>
                  typeof item === "number" ? (
                    <PaginationItem key={`page-${item}`}>
                      <PaginationLink
                        href="#"
                        isActive={item === currentPage}
                        onClick={(event) => {
                          event.preventDefault();
                          setCurrentPage(item);
                          scrollToCatalog();
                        }}
                      >
                        {item}
                      </PaginationLink>
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={item}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )
                )}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1));
                      scrollToCatalog();
                    }}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </section>
  );
};

export default ShopSection;



